import { Plus, X } from 'lucide-react'

import { Combobox } from '@/components/Combobox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

import { FieldLabel, Hint } from './_shared'
import { newCreateIssueRule } from './config-types'
import type {
  BambooField,
  BambooProfileBlock,
  BambooRole,
  ChecklistStep,
  CreateIssueButtonBlock,
  CreateIssueFieldRule,
  CreateIssueRuleSource,
  CustomHtmlBlock,
  FieldColumn,
  HeadingBlock,
  JqlTableBlock,
  ProgressChecklistBlock,
  FinRenewalButtonBlock,
  SlackDmButtonBlock,
} from './config-types'

const PLACEHOLDER_HINT = 'Placeholders: {{issueKey}} {{projectKey}} {{fields.customfield_N}}'
const DONE_CATEGORIES = ['done', 'indeterminate', 'new'] as const

// ────────────────────────────────────────────────────────────────────────────
// Reusable atoms (shared by the block inspector + FieldRow)
// ────────────────────────────────────────────────────────────────────────────

export function IconButton({
  onClick,
  label,
  children,
}: {
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-line-strong text-text-2 transition-colors hover:border-danger hover:text-danger"
    >
      {children}
    </button>
  )
}

/** (field_id, label?) pair list with add/remove — used for jql_table columns. */
export function FieldPairList({
  pairs,
  onChange,
  addLabel,
}: {
  pairs: FieldColumn[]
  onChange: (next: FieldColumn[]) => void
  addLabel: string
}) {
  return (
    <div className="space-y-2">
      {pairs.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="flex-1">
            <Combobox
              source="field"
              deps={{}}
              multi={false}
              value={p.field_id}
              onChange={(v) => onChange(pairs.map((x, idx) => (idx === i ? { ...x, field_id: v } : x)))}
            />
          </div>
          <Input
            value={p.label ?? ''}
            onChange={(e) => onChange(pairs.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
            placeholder="label (optional)"
            className="flex-1 font-mono text-xs"
          />
          <IconButton label="Remove column" onClick={() => onChange(pairs.filter((_, idx) => idx !== i))}>
            <X className="size-3.5" aria-hidden />
          </IconButton>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...pairs, { field_id: '' }])}>
        <Plus className="mr-1 size-3" aria-hidden />
        {addLabel}
      </Button>
    </div>
  )
}

export function DoneCategories({
  selected,
  onChange,
}: {
  selected: string[]
  onChange: (next: string[]) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {DONE_CATEGORIES.map((c) => {
        const on = selected.includes(c)
        return (
          <button
            key={c}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? selected.filter((x) => x !== c) : [...selected, c])}
            className={[
              'rounded border px-2 py-0.5 font-mono text-[11.5px] transition-colors',
              on ? 'border-primary bg-surface-active text-foreground' : 'border-line-strong text-text-2 hover:text-foreground',
            ].join(' ')}
          >
            {c}
          </button>
        )
      })}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Per-type inspector bodies — each edits one block of its type.
// ────────────────────────────────────────────────────────────────────────────

export function HeadingBody({
  block,
  onChange,
}: {
  block: HeadingBlock
  onChange: (b: HeadingBlock) => void
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel>text</FieldLabel>
      <Input
        value={block.text}
        onChange={(e) => onChange({ ...block, text: e.target.value })}
        placeholder="e.g. Task"
        className="font-mono text-sm"
      />
    </div>
  )
}

export function JqlTableBody({
  block,
  onChange,
}: {
  block: JqlTableBlock
  onChange: (b: JqlTableBlock) => void
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>jql template</FieldLabel>
        <Textarea
          value={block.jql_template}
          onChange={(e) => onChange({ ...block, jql_template: e.target.value })}
          rows={3}
          spellCheck={false}
          placeholder={'parent = {{issueKey}}'}
          className="font-mono text-xs"
        />
        <Hint>{PLACEHOLDER_HINT}</Hint>
        <Hint tone="warn">
          Single quotes are not allowed; {'{{fields.*}}'} may only appear inside double-quoted literals
          (the backend rejects otherwise).
        </Hint>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>columns</FieldLabel>
        <FieldPairList
          pairs={block.columns}
          onChange={(columns) => onChange({ ...block, columns })}
          addLabel="Add column"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <FieldLabel>max rows</FieldLabel>
          <Input
            type="number"
            min={1}
            max={100}
            value={block.max_rows}
            onChange={(e) => {
              const n = Number(e.target.value)
              onChange({ ...block, max_rows: Number.isFinite(n) ? n : 20 })
            }}
            className="font-mono text-xs"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>order by</FieldLabel>
          <Input
            value={block.order_by ?? ''}
            onChange={(e) => onChange({ ...block, order_by: e.target.value })}
            placeholder="e.g. created DESC"
            className="font-mono text-xs"
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel>row numbers</FieldLabel>
        <Switch
          checked={block.show_row_numbers ?? false}
          onCheckedChange={(show_row_numbers) => onChange({ ...block, show_row_numbers })}
          aria-label="show a row number column"
        />
      </div>
      <Hint>
        Adds a leading “#” column. The issue key is always the first data column — no need to add
        it manually.
      </Hint>
    </div>
  )
}

export function ProgressChecklistBody({
  block,
  onChange,
}: {
  block: ProgressChecklistBlock
  onChange: (b: ProgressChecklistBlock) => void
}) {
  function updateStep(i: number, patch: Partial<ChecklistStep>) {
    onChange({ ...block, steps: block.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) })
  }
  return (
    <div className="space-y-3">
      {block.steps.map((step, i) => (
        <div key={i} className="space-y-2.5 rounded-md border border-line p-3">
          <div className="flex items-center gap-2">
            <Input
              value={step.label}
              onChange={(e) => updateStep(i, { label: e.target.value })}
              placeholder={`step ${i + 1} label`}
              className="flex-1 font-mono text-xs"
            />
            <IconButton
              label="Remove step"
              onClick={() => onChange({ ...block, steps: block.steps.filter((_, idx) => idx !== i) })}
            >
              <X className="size-3.5" aria-hidden />
            </IconButton>
          </div>
          <Textarea
            value={step.jql_template}
            onChange={(e) => updateStep(i, { jql_template: e.target.value })}
            rows={2}
            spellCheck={false}
            placeholder={'issuetype = Sub-task AND parent = {{issueKey}}'}
            className="font-mono text-xs"
          />
          <div className="space-y-1.5">
            <FieldLabel>done when status category is</FieldLabel>
            <DoneCategories
              selected={step.done_categories}
              onChange={(done_categories) => updateStep(i, { done_categories })}
            />
          </div>
        </div>
      ))}
      <Hint>{PLACEHOLDER_HINT}</Hint>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onChange({ ...block, steps: [...block.steps, { label: '', jql_template: '', done_categories: ['done'] }] })
        }
      >
        <Plus className="mr-1 size-3" aria-hidden />
        Add step
      </Button>
    </div>
  )
}

export function SlackDmButtonBody({
  block,
  onChange,
}: {
  block: SlackDmButtonBlock
  onChange: (b: SlackDmButtonBlock) => void
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>label</FieldLabel>
        <Input
          value={block.label}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="Slack msg"
          className="font-mono text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>default message</FieldLabel>
        <Textarea
          value={block.default_message}
          onChange={(e) => onChange({ ...block, default_message: e.target.value })}
          rows={4}
          spellCheck={false}
          className="font-mono text-xs"
        />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>issue type contains</FieldLabel>
        <Input
          value={block.issue_type_contains}
          onChange={(e) => onChange({ ...block, issue_type_contains: e.target.value })}
          placeholder="e.g. Payment"
          className="font-mono text-xs"
        />
        <Hint>Substring of the issue type name; empty = all types.</Hint>
      </div>
      <div className="flex items-center justify-between gap-3">
        <FieldLabel>preselect reporter</FieldLabel>
        <Switch
          checked={block.preselect_reporter}
          onCheckedChange={(preselect_reporter) => onChange({ ...block, preselect_reporter })}
          aria-label="preselect reporter as recipient"
        />
      </div>
      <Hint>Preselect the reporter as a recipient.</Hint>
      <Hint tone="warn">
        The button renders only in the Forge panel (custom UI); embed/SR4JC pages ignore this block.
      </Hint>
    </div>
  )
}

export function FinRenewalButtonBody({
  block,
  onChange,
}: {
  block: FinRenewalButtonBlock
  onChange: (b: FinRenewalButtonBlock) => void
}) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>scenario</FieldLabel>
        <select
          value={block.scenario}
          onChange={(e) =>
            onChange({ ...block, scenario: e.target.value as FinRenewalButtonBlock['scenario'] })
          }
          className="h-8 w-full rounded-md border border-input bg-surface px-2 font-mono text-[12.5px] text-foreground"
        >
          <option value="host_fin">HOST &rarr; FIN</option>
          <option value="dom_fin">DOM &rarr; FIN</option>
          <option value="dom_finorg">DOM &rarr; FINORG</option>
          <option value="dom_auto">DOM &rarr; auto (FIN/FINORG by Organization)</option>
        </select>
        <Hint>Picks the endpoint and the issue-key prefix the button accepts.</Hint>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>label</FieldLabel>
        <Input
          value={block.label}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="Створити Renewal"
          className="font-mono text-sm"
        />
        {block.scenario === 'dom_auto' && <Hint>Shown when the issue routes to FIN.</Hint>}
      </div>
      {block.scenario === 'dom_auto' && (
        <>
          <div className="space-y-1.5">
            <FieldLabel>FINORG label</FieldLabel>
            <Input
              value={block.finorg_label ?? ''}
              onChange={(e) => onChange({ ...block, finorg_label: e.target.value })}
              placeholder="Створити FINORG Renewal"
              className="font-mono text-sm"
            />
            <Hint>Shown when the issue routes to FINORG.</Hint>
          </div>
          <div className="space-y-1.5">
            <FieldLabel>FIN organizations</FieldLabel>
            <Input
              value={(block.fin_organizations ?? []).join(',')}
              onChange={(e) =>
                onChange({ ...block, fin_organizations: e.target.value.split(',') })
              }
              placeholder="Boosta"
              className="font-mono text-xs"
            />
            <Hint>
              Comma-separated Organization values routed to FIN; any other value routes to
              FINORG, an empty field routes to FIN.
            </Hint>
          </div>
          <div className="space-y-1.5">
            <FieldLabel>Organization field id</FieldLabel>
            <Input
              value={block.organization_field_id ?? ''}
              onChange={(e) => onChange({ ...block, organization_field_id: e.target.value })}
              placeholder="customfield_10286"
              className="font-mono text-xs"
            />
          </div>
        </>
      )}
      <div className="space-y-1.5">
        <FieldLabel>issue type contains</FieldLabel>
        <Input
          value={block.issue_type_contains}
          onChange={(e) => onChange({ ...block, issue_type_contains: e.target.value })}
          placeholder="e.g. Host"
          className="font-mono text-xs"
        />
        <Hint>Substring of the issue type name; empty = all types.</Hint>
      </div>
      <Hint tone="warn">
        Renders in the Forge panel and in this preview only; the public embed page ignores this
        block. Authorization stays with the old-Jira endpoint.
      </Hint>
    </div>
  )
}

const RULE_SOURCES: { value: CreateIssueRuleSource; label: string }[] = [
  { value: 'copy', label: 'copy from source field' },
  { value: 'template', label: 'template (prefix + field)' },
  { value: 'const', label: 'constant value' },
  { value: 'date_offset', label: 'date = today + N days' },
]

const SELECT_CLS =
  'h-8 w-full rounded-md border border-input bg-surface px-2 font-mono text-[12.5px] text-foreground'

function CreateIssueRuleRow({
  rule,
  index,
  onChange,
  onRemove,
}: {
  rule: CreateIssueFieldRule
  index: number
  onChange: (r: CreateIssueFieldRule) => void
  onRemove: () => void
}) {
  const n = index + 1
  const users = Array.isArray(rule.value)
  const setSource = (source: CreateIssueRuleSource) =>
    onChange({ ...rule, source, value: source === 'const' ? '' : null })
  return (
    <li className="rounded-md border border-line-strong bg-surface-raised/40 p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.07em] text-text-3">
          rule {n}
        </span>
        <IconButton onClick={onRemove} label={`Remove rule ${n}`}>
          <X className="size-3.5" aria-hidden />
        </IconButton>
      </div>
      <div className="space-y-2.5">
        <div>
          <FieldLabel>target field</FieldLabel>
          <Combobox
            source="field"
            deps={{}}
            multi={false}
            value={rule.target}
            onChange={(target) => onChange({ ...rule, target })}
            ariaLabel={`Rule ${n} target`}
          />
        </div>
        <div>
          <FieldLabel>source</FieldLabel>
          <select
            value={rule.source}
            onChange={(e) => setSource(e.target.value as CreateIssueRuleSource)}
            aria-label={`Rule ${n} source`}
            className={`mt-1 ${SELECT_CLS}`}
          >
            {RULE_SOURCES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {(rule.source === 'copy' || rule.source === 'template') && (
          <div>
            <FieldLabel>from field (on the source issue)</FieldLabel>
            <Combobox
              source="field"
              deps={{}}
              multi={false}
              value={rule.from_field}
              onChange={(from_field) => onChange({ ...rule, from_field })}
              ariaLabel={`Rule ${n} from field`}
            />
          </div>
        )}
        {rule.source === 'template' && (
          <div>
            <FieldLabel>prefix</FieldLabel>
            <Textarea
              value={rule.prefix}
              onChange={(e) => onChange({ ...rule, prefix: e.target.value })}
              rows={2}
              spellCheck={false}
              aria-label={`Rule ${n} prefix`}
              placeholder="IT Deploy: "
              className="mt-1 font-mono text-xs"
            />
            <Hint>Written before the field value.</Hint>
          </div>
        )}
        {rule.source === 'const' && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <FieldLabel>value</FieldLabel>
              <div role="group" aria-label={`Rule ${n} value kind`} className="flex gap-px text-[11.5px]">
                {(['text', 'users'] as const).map((k) => {
                  const on = (k === 'users') === users
                  return (
                    <button
                      key={k}
                      type="button"
                      aria-pressed={on}
                      onClick={() => !on && onChange({ ...rule, value: k === 'users' ? [] : '' })}
                      className={`rounded px-2 py-0.5 transition-colors ${
                        on ? 'bg-surface-selected text-foreground' : 'text-text-3 hover:text-foreground'
                      }`}
                    >
                      {k === 'text' ? 'text' : 'користувачі'}
                    </button>
                  )
                })}
              </div>
            </div>
            {users ? (
              <Combobox
                source="user"
                deps={{}}
                multi
                value={(rule.value as string[]).join('\n')}
                onChange={(v) =>
                  onChange({ ...rule, value: v.split('\n').map((x) => x.trim()).filter(Boolean) })
                }
                ariaLabel={`Rule ${n} users`}
              />
            ) : (
              <Input
                value={typeof rule.value === 'string' ? rule.value : ''}
                onChange={(e) => onChange({ ...rule, value: e.target.value })}
                aria-label={`Rule ${n} value`}
                className="mt-1 font-mono text-xs"
              />
            )}
          </div>
        )}
        {rule.source === 'date_offset' && (
          <div className="flex items-end gap-3">
            <div className="w-24">
              <FieldLabel>days</FieldLabel>
              <Input
                type="number"
                min={0}
                max={30}
                value={rule.days}
                onChange={(e) => onChange({ ...rule, days: e.target.value === '' ? 0 : Number(e.target.value) })}
                aria-label={`Rule ${n} days`}
                className="mt-1 font-mono text-xs"
              />
            </div>
            <div className="flex h-8 items-center gap-2">
              <Switch
                checked={rule.working_days}
                onCheckedChange={(working_days) => onChange({ ...rule, working_days })}
                aria-label={`Rule ${n} working days`}
              />
              <span className="text-[12px] text-text-2">working days</span>
            </div>
          </div>
        )}
      </div>
    </li>
  )
}

export function CreateIssueButtonBody({
  block,
  onChange,
}: {
  block: CreateIssueButtonBlock
  onChange: (b: CreateIssueButtonBlock) => void
}) {
  const setRule = (i: number, r: CreateIssueFieldRule) =>
    onChange({ ...block, fields: block.fields.map((x, idx) => (idx === i ? r : x)) })
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>label</FieldLabel>
        <Input
          value={block.label}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          aria-label="Button label"
          maxLength={60}
          placeholder="Створити задачу"
          className="font-mono text-sm"
        />
      </div>
      <div className="space-y-1.5">
        <FieldLabel>show on issue types</FieldLabel>
        <Combobox
          source="issuetype"
          valueKey="name"
          deps={{}}
          multi
          value={block.issue_types.join('\n')}
          onChange={(v) =>
            onChange({ ...block, issue_types: v.split('\n').map((x) => x.trim()).filter(Boolean) })
          }
          ariaLabel="Issue types"
        />
        <Hint>Exact issue type names of the source issue.</Hint>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel>target project</FieldLabel>
          <Combobox
            source="project"
            deps={{}}
            multi={false}
            value={block.target_project_id}
            onChange={(target_project_id) => onChange({ ...block, target_project_id })}
            ariaLabel="Target project"
          />
        </div>
        <div className="space-y-1.5">
          <FieldLabel>target issue type</FieldLabel>
          <Combobox
            source="issuetype"
            deps={{}}
            multi={false}
            value={block.target_issue_type_id}
            onChange={(target_issue_type_id) => onChange({ ...block, target_issue_type_id })}
            ariaLabel="Target issue type"
          />
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel>field rules ({block.fields.length})</FieldLabel>
        {block.fields.length > 0 && (
          <ul aria-label="Field rules" className="space-y-2">
            {block.fields.map((r, i) => (
              <CreateIssueRuleRow
                key={i}
                rule={r}
                index={i}
                onChange={(next) => setRule(i, next)}
                onRemove={() => onChange({ ...block, fields: block.fields.filter((_, idx) => idx !== i) })}
              />
            ))}
          </ul>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange({ ...block, fields: [...block.fields, newCreateIssueRule()] })}
        >
          <Plus className="mr-1 size-3" aria-hidden />
          Add rule
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <FieldLabel>link type id</FieldLabel>
          <Input
            value={block.link_type_id}
            inputMode="numeric"
            onChange={(e) => onChange({ ...block, link_type_id: e.target.value.replace(/\D/g, '') })}
            aria-label="Link type id"
            placeholder="10009"
            className="font-mono text-xs"
          />
          <Hint>Empty = no link.</Hint>
        </div>
        <div className="space-y-1.5">
          <FieldLabel>direction</FieldLabel>
          <select
            value={block.link_direction}
            onChange={(e) =>
              onChange({ ...block, link_direction: e.target.value as CreateIssueButtonBlock['link_direction'] })
            }
            aria-label="Link direction"
            className={SELECT_CLS}
          >
            <option value="source_outward">вихідна задача → нова (connects to)</option>
            <option value="source_inward">нова задача → вихідна (connected from)</option>
          </select>
        </div>
      </div>
      <Hint tone="warn">
        Active in the Forge panel only; the preview and the public embed show it disabled or not at all.
      </Hint>
    </div>
  )
}

export function CustomHtmlBody({
  block,
  onChange,
}: {
  block: CustomHtmlBlock
  onChange: (b: CustomHtmlBlock) => void
}) {
  return (
    <div className="space-y-1.5">
      <FieldLabel>html</FieldLabel>
      <Textarea
        value={block.html}
        onChange={(e) => onChange({ ...block, html: e.target.value })}
        rows={10}
        spellCheck={false}
        placeholder={'<p>Issue: {{issueKey}}</p>'}
        className="font-mono text-xs"
      />
      <Hint>Supports {'{{issueKey}}'} and {'{{projectKey}}'}.</Hint>
      <Hint tone="warn">
        script / iframe tags and on* handlers are stripped (nh3 sanitizer) when saved.
      </Hint>
    </div>
  )
}

const BAMBOO_ROLE_OPTIONS: { value: BambooRole; label: string }[] = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'reporter', label: 'Reporter' },
]
const BAMBOO_FIELD_OPTIONS: { value: BambooField; label: string }[] = [
  { value: 'job_title', label: 'Job Title' },
  { value: 'department', label: 'Department' },
  { value: 'division', label: 'Division' },
  { value: 'finproject', label: 'Fin project' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'status', label: 'Status' },
]

/** Toggle с сохранением канонического порядка опций; снять последний нельзя (min 1). */
function toggleInOrder<T extends string>(current: T[], value: T, order: T[]): T[] {
  const next = current.includes(value)
    ? current.filter((x) => x !== value)
    : order.filter((x) => current.includes(x) || x === value)
  return next.length ? next : current
}

export function BambooProfileBody({
  block,
  onChange,
}: {
  block: BambooProfileBlock
  onChange: (b: BambooProfileBlock) => void
}) {
  const roleOrder = BAMBOO_ROLE_OPTIONS.map((o) => o.value)
  const fieldOrder = BAMBOO_FIELD_OPTIONS.map((o) => o.value)
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <FieldLabel>show</FieldLabel>
        <div className="flex gap-4">
          {BAMBOO_ROLE_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-primary"
                checked={block.show.includes(o.value)}
                disabled={block.show.length === 1 && block.show.includes(o.value)}
                onChange={() =>
                  onChange({ ...block, show: toggleInOrder(block.show, o.value, roleOrder) })
                }
              />
              {o.label}
            </label>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        <FieldLabel>fields</FieldLabel>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {BAMBOO_FIELD_OPTIONS.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-primary"
                checked={block.fields.includes(o.value)}
                disabled={block.fields.length === 1 && block.fields.includes(o.value)}
                onChange={() =>
                  onChange({ ...block, fields: toggleInOrder(block.fields, o.value, fieldOrder) })
                }
              />
              {o.label}
            </label>
          ))}
        </div>
        <Hint>BambooHR data for the issue people; unmatched users render as “—”.</Hint>
      </div>
    </div>
  )
}
